<template>
  <div
    v-if="user && user.id"
    class="notice-center"
  >
    <!-- Informativo obrigatorio: trava a tela ate marcar como lido; sem ESC e sem clicar fora -->
    <v-dialog
      :value="!!blockingNotice"
      persistent
      no-click-animation
      max-width="720px"
      content-class="notice-center__dialog"
      @keydown.esc.prevent
    >
      <v-card
        v-if="blockingNotice"
        data-test="informativo-obrigatorio"
      >
        <v-card-title class="notice-center__title">
          <PhWarningCircle
            :size="22"
            class="mr-2"
          />
          {{ blockingNotice.title }}
          <Chip
            v-if="blockingNotice.urgent"
            small
            variant="tonal"
            tonal-color="error"
            class="ml-2"
            data-test="informativo-urgente-selo"
          >{{ $t('notices.form.urgent') }}</Chip>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div
            class="notice-center__content"
            v-html="blockingNotice.content"
          ></div>
          <p
            v-if="blockingRemaining > 0"
            class="notice-center__remaining"
          >{{ $tc('notices.center.remaining', blockingRemaining) }}</p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-end">
          <Button
            variant="primary"
            :loading="acknowledging === blockingNotice.id"
            data-test="botao-li-e-estou-ciente"
            @click="acknowledge(blockingNotice)"
          >
            {{ $t('notices.center.acknowledge') }}
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Informativos sem confirmacao: faixa no topo, fecham ao marcar como lido -->
    <div
      v-if="infoNotices.length"
      class="notice-center__banners"
      data-test="informativos-faixa"
    >
      <v-alert
        v-for="notice in infoNotices"
        :key="notice.id"
        :type="notice.urgent ? 'error' : 'info'"
        text
        dense
        border="left"
        class="notice-center__banner"
        :data-test="`informativo-${notice.id}`"
      >
        <div class="notice-center__banner-body">
          <div>
            <strong>{{ notice.title }}</strong>
            <div
              class="notice-center__content notice-center__content--compact"
              v-html="notice.content"
            ></div>
          </div>
          <Button
            variant="tertiary"
            :loading="acknowledging === notice.id"
            :data-test="`marcar-lido-${notice.id}`"
            @click="acknowledge(notice)"
          >
            {{ $t('notices.center.markRead') }}
          </Button>
        </div>
      </v-alert>
    </div>
  </div>
</template>

<script>
// Components
import Button from '@/components/comum/Button.vue';
import Chip from '@/components/comum/Chip.vue';
import { PhWarningCircle } from 'phosphor-vue';

// APIs
import NoticeApi from '@/api/notice/notice-api';

// Utils
import utilsStorage from '@/utils/storage';

/* Marca do login: um token novo e um login novo. F5 e troca de tela mantem o token. */
const SESSION_KEY = 'notices-session-tc';

function _readSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
  } catch {
    return null;
  }
}

function _tokenMark() {
  return String(utilsStorage.getTokenOnStorage() || '').slice(-24);
}

/**
 * Comeco da sessao atual. Token diferente do guardado = acabou de logar, e o
 * momento vira agora; senao segue o login ja registrado.
 * @returns {{ isLogin: boolean, loginAt: number }}
 */
function _resolveSession() {
  const mark = _tokenMark();
  const saved = _readSession();

  if (saved && saved.mark === mark && saved.loginAt) return { isLogin: false, loginAt: saved.loginAt };

  const loginAt = Date.now();

  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ mark, loginAt }));
  } catch {
    /* Sem storage cada carregamento conta como login */
  }

  return { isLogin: true, loginAt };
}

/**
 * Fora do login, so entra o que nao pode esperar: o urgente, e o obrigatorio que ja
 * estava valendo no login (F5 nao pode servir para pular a leitura).
 */
function _visibleAfterLogin(notice, loginAt) {
  if (notice.urgent) return true;

  const createdAt = new Date(notice.createdAt || notice.startsAt).getTime();

  return !!notice.requiresAck && createdAt <= loginAt;
}

export default {
  name: 'NoticeCenter',

  components: { Button, Chip, PhWarningCircle },

  props: {
    user: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      notices: [],
      acknowledging: null,
      api: null,
      loginAt: null
    };
  },

  computed: {
    blockingNotices() {
      return this.notices.filter((notice) => notice.requiresAck);
    },

    blockingNotice() {
      return this.blockingNotices[0] || null;
    },

    blockingRemaining() {
      return Math.max(0, this.blockingNotices.length - 1);
    },

    infoNotices() {
      return this.notices.filter((notice) => !notice.requiresAck);
    }
  },

  watch: {
    'user.id': {
      immediate: true,
      handler(id) {
        if (id) this.loadOnSessionStart();
        else this.notices = [];
      }
    },

    /* Trocar de tela confere de novo, mas so traz o urgente publicado nesse meio tempo */
    '$route.path'() {
      if (this.user?.id) this.load();
    }
  },

  mounted() {
    this.$eventBus.$on('notices-updated', this.load);
  },

  beforeDestroy() {
    this.$eventBus.$off('notices-updated', this.load);
  },

  methods: {
    _api() {
      if (!this.api) this.api = new NoticeApi(this.user);

      return this.api;
    },

    /** No login mostra tudo o que esta pendente; num F5 vale a regra de depois do login. */
    async loadOnSessionStart() {
      const { isLogin, loginAt } = _resolveSession();

      this.loginAt = loginAt;
      await this.load({ all: isLogin });
    },

    /**
     * Busca os pendentes. Fora do login mantem o que ja esta na tela (se continua
     * pendente, ou seja, nao foi cancelado nem lido) e so acrescenta o permitido.
     */
    async load({ all = false } = {}) {
      try {
        const response = await this._api().findPending();
        const pending = response.data || [];

        if (all) {
          this.notices = pending;

          return;
        }

        const shownIds = new Set(this.notices.map((notice) => notice.id));

        this.notices = pending.filter((notice) => shownIds.has(notice.id) || _visibleAfterLogin(notice, this.loginAt));
      } catch {
        /* Sem resposta a tela segue; o proximo carregamento tenta de novo */
      }
    },

    async acknowledge(notice) {
      this.acknowledging = notice.id;

      try {
        await this._api().markRead(notice.id);
        this.notices = this.notices.filter((item) => item.id !== notice.id);
      } catch (error) {
        this.$root.$emit('sendMessage', this.$t('notices.center.ackError'), 'error');
      } finally {
        this.acknowledging = null;
      }
    }
  }
};
</script>

<style scoped>
.notice-center__title {
  gap: var(--tc-space-1);
  font-size: var(--tc-fs-h4);
  font-weight: var(--tc-fw-semibold);
}

.notice-center__content {
  font-size: var(--tc-fs-sm);
  line-height: 1.5;
  color: var(--tc-text);
}

.notice-center__content--compact {
  margin-top: var(--tc-space-1);
}

.notice-center__remaining {
  margin: var(--tc-space-3) 0 0;
  font-size: var(--tc-fs-label);
  color: var(--tc-text-2);
}

.notice-center__banners {
  padding: var(--tc-space-2) var(--tc-space-3) 0;
}

.notice-center__banner {
  margin-bottom: var(--tc-space-2);
}

.notice-center__banner-body {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--tc-space-3);
  flex-wrap: wrap;
}
</style>
